import React, { useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import { copyCouponContent, deleteCouponContent, fetchCouponContents } from "../api";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ActionIcon, Anchor, Badge, Box, Button, Center, Group, Loader, Table, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { CopyIcon, PencilSimpleIcon, PlusIcon, TicketIcon, TrashIcon } from "@phosphor-icons/react";
import CouponContentFormModal from "../components/CouponContentFormModal";
import { confirmCopy, confirmDelete } from "../utils/confirm";

interface Props {
  client: Client
}

type CouponContentRow = {
  id: number
  to_type: string
  title: string
  is_active: boolean
}

const CouponContentContainer: React.FC<Props> = ({ client }) => {
  const { data: couponContents, mutate } = useSWR<CouponContentRow[]>(
    `/admin/clients/${client.id}/coupon_contents`,
    () => fetchCouponContents(client.id),
  )

  const [openedForm, formModal] = useDisclosure(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const openCreate = () => {
    setEditingId(null)
    formModal.open()
  }

  const openEdit = (id: number) => {
    setEditingId(id)
    formModal.open()
  }

  const handleCopy = async (id: number) => {
    if (!confirmCopy('クーポン画面')) return
    try {
      await copyCouponContent(client.id, id)
      await mutate()
      notifications.show({ message: 'クーポン画面をコピーしました', color: 'teal' })
    } catch (e) {
      console.error('クーポン画面のコピーに失敗しました', e)
      notifications.show({ message: 'コピーに失敗しました', color: 'red' })
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirmDelete('クーポン画面')) return
    try {
      await deleteCouponContent(client.id, id)
      await mutate()
      notifications.show({ message: 'クーポン画面を削除しました', color: 'teal' })
    } catch (e) {
      console.error('クーポン画面の削除に失敗しました', e)
      notifications.show({ message: '削除に失敗しました', color: 'red' })
    }
  }

  if (!couponContents) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  return (
    <Box>
      <Group gap={12} mb={24}>
        <ThemeIcon variant="transparent" color="#4BC1C7">
          <TicketIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          クーポン画面設定
        </Title>
      </Group>

      <Text fz={14} c="#000000CC">
        ※設定方法については、
        <Anchor
          href="https://www.creativehope.co.jp/invyknowledge/notification/02"
          target="_blank"
          rel="noopener noreferrer"
          fz={14}
          underline='always'
        >
          マニュアル（invyご利用ガイド）
        </Anchor>
        をご確認ください。<br />
        ※本番設定が複数ある場合はIDが若いものが優先されます。
      </Text>
      <Text fz={14} c="#000000CC" mt={16}>
        こちらでは、クーポン内容の設定を行います。
      </Text>

      <Button
        variant="filled"
        radius="xl"
        leftSection={<PlusIcon size={16} weight="bold" />}
        onClick={openCreate}
        mt={40}
      >
        新規作成
      </Button>

      <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden', marginTop: 16 }}>
        <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={110}>ID</Table.Th>
              <Table.Th w={110}>有効</Table.Th>
              <Table.Th>タイプ</Table.Th>
              <Table.Th>ページタイトル</Table.Th>
              <Table.Th w={160} ta="center">プレビュー</Table.Th>
              <Table.Th w={80} ta="center">編集</Table.Th>
              <Table.Th w={80} ta="center">コピー</Table.Th>
              <Table.Th w={80} ta="center">削除</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {couponContents.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="#00000099" ta="center" py="md">
                    クーポン内容が登録されていません
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              couponContents.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.id}</Table.Td>
                  <Table.Td>
                    <Badge color={item.is_active ? 'invy' : 'gray'} variant="light">
                      {item.is_active ? '有効' : '無効'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {item.to_type === 'inviter'
                      ? '紹介者'
                      : item.to_type === 'guest'
                        ? 'ゲスト'
                        : ''}
                  </Table.Td>
                  <Table.Td>{item.title}</Table.Td>
                  <Table.Td ta="center">
                    <Anchor
                      href={`/admin/api/clients/${client.id}/coupon_contents/${item.id}/preview`}
                      target="_blank"
                      rel="noopener noreferrer"
                      fz={13}
                    >
                      プレビュー
                    </Anchor>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="編集">
                      <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(item.id)}>
                        <PencilSimpleIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="コピー">
                      <ActionIcon variant="subtle" color="gray" onClick={() => handleCopy(item.id)}>
                        <CopyIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="削除">
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(item.id)}>
                        <TrashIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      <CouponContentFormModal
        opened={openedForm}
        onClose={formModal.close}
        client={client}
        couponContentId={editingId}
        onSaved={mutate}
      />
    </Box>
  )
}

export default CouponContentContainer
